import {
  readStorageItemState,
  removeStorageItem,
  writeStorageItem,
} from '@/components/Grid/storage'

export type GridViewPreferenceSnapshot = {
  preferenceId: string
  organizationID: string
  scopeKey: string
  gridSchemaVersion: string
  payloadVersion: string
  selectedViewRef: string | null
  draftPayload: string | null
  expectedRevision: number | null
}

export type GridViewPreferenceWriteResult = {
  status: 'APPLIED' | 'CONFLICT'
  revision?: number | null
}

export type GridViewPreferenceCoordinatorState =
  | 'idle'
  | 'pending'
  | 'error'
  | 'conflict'

export type GridViewPreferenceConflict = {
  local: GridViewPreferenceSnapshot
  remoteRevision: number | null
}

export type GridViewPreferenceConflictChoice = 'local' | 'remote'

export type GridViewPreferenceWriter = (
  snapshot: GridViewPreferenceSnapshot,
) => Promise<GridViewPreferenceWriteResult>

export type GridViewPreferenceCoordinator = {
  enqueue: (snapshot: GridViewPreferenceSnapshot) => void
  flush: () => Promise<void>
  retry: () => Promise<void>
  resolveConflict: (
    choice: GridViewPreferenceConflictChoice,
    remoteRevision?: number | null,
  ) => Promise<void>
  hydrateRemoteRevision: (revision: number) => void
  setWriter: (writer: GridViewPreferenceWriter) => void
  retain: () => void
  release: () => void
  getState: () => GridViewPreferenceCoordinatorState
  getPendingSnapshot: () => GridViewPreferenceSnapshot | null
  getLastAcknowledgedSnapshot: () => GridViewPreferenceSnapshot | null
  getConflict: () => GridViewPreferenceConflict | null
  subscribe: (listener: () => void) => () => void
}

const DEBOUNCE_MS = 250
const OUTBOX_PREFIX = 'grid-view-preference-outbox'
const ACK_PREFIX = 'grid-view-preference-ack'
export const GRID_VIEW_PREFERENCE_ACK_TTL_MS = 5 * 60 * 1000

type PersistedAcknowledgement = {
  snapshot: GridViewPreferenceSnapshot
  savedAt: number
  writeId: string | null
}

type PersistedOutbox = {
  snapshot: GridViewPreferenceSnapshot
  writeId: string | null
}

const isSnapshot = (value: unknown): value is GridViewPreferenceSnapshot => {
  if (!value || typeof value !== 'object') return false
  const snapshot = value as Partial<GridViewPreferenceSnapshot>
  return (
    typeof snapshot.preferenceId === 'string' &&
    typeof snapshot.organizationID === 'string' &&
    typeof snapshot.scopeKey === 'string' &&
    typeof snapshot.gridSchemaVersion === 'string' &&
    typeof snapshot.payloadVersion === 'string' &&
    (snapshot.selectedViewRef === null ||
      typeof snapshot.selectedViewRef === 'string') &&
    (snapshot.draftPayload === null ||
      typeof snapshot.draftPayload === 'string') &&
    (snapshot.expectedRevision === null ||
      (typeof snapshot.expectedRevision === 'number' &&
        Number.isInteger(snapshot.expectedRevision) &&
        snapshot.expectedRevision >= 0))
  )
}

const sameSnapshot = (
  left: GridViewPreferenceSnapshot,
  right: GridViewPreferenceSnapshot,
) =>
  left.preferenceId === right.preferenceId &&
  left.organizationID === right.organizationID &&
  left.scopeKey === right.scopeKey &&
  left.gridSchemaVersion === right.gridSchemaVersion &&
  left.payloadVersion === right.payloadVersion &&
  left.selectedViewRef === right.selectedViewRef &&
  left.draftPayload === right.draftPayload &&
  left.expectedRevision === right.expectedRevision

const sameSnapshotContent = (
  left: GridViewPreferenceSnapshot,
  right: GridViewPreferenceSnapshot,
) => sameSnapshot(left, { ...right, expectedRevision: left.expectedRevision })

const isWriteId = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

const createWriteId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }
  return `grid-view-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const samePersistedOutbox = (
  outbox: PersistedOutbox,
  snapshot: GridViewPreferenceSnapshot,
  writeId: string | null,
) => outbox.writeId === writeId && sameSnapshot(outbox.snapshot, snapshot)

const acknowledgesOutboxEntry = (
  outbox: PersistedOutbox,
  acknowledgement: PersistedAcknowledgement,
) => {
  if (outbox.writeId !== null || acknowledgement.writeId !== null) {
    return (
      outbox.writeId !== null &&
      outbox.writeId === acknowledgement.writeId &&
      sameSnapshotContent(outbox.snapshot, acknowledgement.snapshot)
    )
  }
  if (!sameSnapshotContent(outbox.snapshot, acknowledgement.snapshot)) {
    return false
  }
  if (acknowledgement.snapshot.expectedRevision === null) {
    return outbox.snapshot.expectedRevision === null
  }
  return (
    outbox.snapshot.expectedRevision === null ||
    outbox.snapshot.expectedRevision <=
      acknowledgement.snapshot.expectedRevision
  )
}

type PendingOutboxCleanup = {
  snapshot: GridViewPreferenceSnapshot
  writeId: string | null
  reason: 'acknowledged' | 'discarded'
  acknowledgedSnapshot?: GridViewPreferenceSnapshot
}

type ParsedStorageValue<T> =
  | { status: 'missing'; value: null }
  | { status: 'value'; value: T }
  | { status: 'unreadable' }

const parseOutbox = (key: string): ParsedStorageValue<PersistedOutbox> => {
  const result = readStorageItemState(key)
  if (result.status === 'unreadable') return { status: 'unreadable' }
  if (result.status === 'missing') return { status: 'missing', value: null }
  const { value: serialized } = result
  try {
    const parsed: unknown = JSON.parse(serialized)
    if (isSnapshot(parsed)) {
      return { status: 'value', value: { snapshot: parsed, writeId: null } }
    }
    if (!parsed || typeof parsed !== 'object') {
      return { status: 'unreadable' }
    }
    const outbox = parsed as Partial<PersistedOutbox>
    if (
      !isSnapshot(outbox.snapshot) ||
      (outbox.writeId !== null && !isWriteId(outbox.writeId))
    ) {
      return { status: 'unreadable' }
    }
    return {
      status: 'value',
      value: { snapshot: outbox.snapshot, writeId: outbox.writeId ?? null },
    }
  } catch {
    return { status: 'unreadable' }
  }
}

const parseAcknowledgement = (
  key: string,
): ParsedStorageValue<PersistedAcknowledgement> => {
  const result = readStorageItemState(key)
  if (result.status === 'unreadable') return { status: 'unreadable' }
  if (result.status === 'missing') return { status: 'missing', value: null }
  const { value: serialized } = result
  try {
    const parsed: unknown = JSON.parse(serialized)
    if (!parsed || typeof parsed !== 'object') {
      return { status: 'unreadable' }
    }
    const acknowledgement = parsed as Partial<PersistedAcknowledgement>
    if (
      typeof acknowledgement.savedAt !== 'number' ||
      !isSnapshot(acknowledgement.snapshot) ||
      (acknowledgement.writeId !== null &&
        acknowledgement.writeId !== undefined &&
        !isWriteId(acknowledgement.writeId))
    ) {
      return { status: 'unreadable' }
    }
    if (
      Date.now() - acknowledgement.savedAt >
      GRID_VIEW_PREFERENCE_ACK_TTL_MS
    ) {
      return removeStorageItem(key)
        ? { status: 'missing', value: null }
        : { status: 'unreadable' }
    }
    return {
      status: 'value',
      value: {
        snapshot: acknowledgement.snapshot,
        savedAt: acknowledgement.savedAt,
        writeId: acknowledgement.writeId ?? null,
      },
    }
  } catch {
    return { status: 'unreadable' }
  }
}

const replaceRevision = (
  snapshot: GridViewPreferenceSnapshot,
  expectedRevision: number,
): GridViewPreferenceSnapshot => ({ ...snapshot, expectedRevision })

const createCoordinator = ({
  key,
  writer,
  onIdle,
}: {
  key: string
  writer: GridViewPreferenceWriter
  onIdle: () => void
}): GridViewPreferenceCoordinator => {
  const outboxKey = `${OUTBOX_PREFIX}:${key}`
  const acknowledgementKey = `${ACK_PREFIX}:${key}`
  const initialOutbox = parseOutbox(outboxKey)
  const initialAcknowledgement = parseAcknowledgement(acknowledgementKey)
  let bootstrapStorageError =
    initialOutbox.status === 'unreadable' ||
    initialAcknowledgement.status === 'unreadable'
  let state: GridViewPreferenceCoordinatorState = bootstrapStorageError
    ? 'error'
    : 'idle'
  let conflict: GridViewPreferenceConflict | null = null
  let pending: GridViewPreferenceSnapshot | null =
    initialOutbox.status === 'value' ? initialOutbox.value.snapshot : null
  let pendingWriteId: string | null =
    initialOutbox.status === 'value' ? initialOutbox.value.writeId : null
  let inFlight: Promise<void> | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let remoteRevision: number | null = null
  let lastAcknowledgedSnapshot =
    initialAcknowledgement.status === 'value'
      ? initialAcknowledgement.value.snapshot
      : null
  let pendingNeedsPersistence = false
  let pendingOutboxCleanup: PendingOutboxCleanup | null = null
  let outboxStorageAvailable = initialOutbox.status !== 'unreadable'
  let hydrated = false
  let activeConsumers = 0
  let currentWriter = writer
  const listeners = new Set<() => void>()

  const observeRemoteRevision = (revision: number | null | undefined) => {
    if (typeof revision === 'number') {
      remoteRevision = Math.max(remoteRevision ?? 0, revision)
    }
    return remoteRevision
  }

  const notify = () => listeners.forEach((listener) => listener())

  const setState = (nextState: GridViewPreferenceCoordinatorState) => {
    if (state === nextState) return
    state = nextState
    notify()
  }

  const writeOutbox = (
    snapshot: GridViewPreferenceSnapshot,
    requestedWriteId = pendingWriteId,
  ) => {
    const writeId = requestedWriteId ?? createWriteId()
    if (pending && sameSnapshot(pending, snapshot)) {
      pendingWriteId = writeId
    }
    const written = writeStorageItem(
      outboxKey,
      JSON.stringify({ writeId, snapshot }),
    )
    outboxStorageAvailable = written
    if (written) pendingNeedsPersistence = false
    return written
  }

  const removeOutboxIfUnchanged = (
    snapshot: GridViewPreferenceSnapshot,
    writeId: string | null,
  ) => {
    const persisted = parseOutbox(outboxKey)
    if (persisted.status === 'unreadable') {
      outboxStorageAvailable = false
      return false
    }
    outboxStorageAvailable = true
    if (persisted.status === 'missing') return true
    if (!samePersistedOutbox(persisted.value, snapshot, writeId)) {
      return false
    }
    const removed = removeStorageItem(outboxKey)
    outboxStorageAvailable = removed
    return removed
  }

  const finishOutboxCleanup = (
    snapshot: GridViewPreferenceSnapshot,
    writeId: string | null,
    reason: PendingOutboxCleanup['reason'],
  ) => {
    if (!removeOutboxIfUnchanged(snapshot, writeId)) {
      pendingOutboxCleanup = { snapshot, writeId, reason }
      setState('error')
      return false
    }
    pendingOutboxCleanup = null
    if (
      pending &&
      sameSnapshot(pending, snapshot) &&
      pendingWriteId === writeId
    ) {
      pending = null
      pendingWriteId = null
    }
    return true
  }

  const writeAcknowledgement = (
    snapshot: GridViewPreferenceSnapshot,
    writeId: string | null,
  ) =>
    writeStorageItem(
      acknowledgementKey,
      JSON.stringify({
        snapshot,
        savedAt: Date.now(),
        writeId,
      } satisfies PersistedAcknowledgement),
    )

  const restoreOutbox = () => {
    const persisted = parseOutbox(outboxKey)
    if (persisted.status === 'unreadable') {
      outboxStorageAvailable = false
      return false
    }
    outboxStorageAvailable = true
    if (!pending && persisted.status === 'value') {
      pending = persisted.value.snapshot
      pendingWriteId = persisted.value.writeId
    }
    return true
  }

  const recoverBootstrapStorage = () => {
    let persistedOutbox = parseOutbox(outboxKey)
    if (persistedOutbox.status === 'unreadable') {
      outboxStorageAvailable = false
      return 'unreadable' as const
    }
    outboxStorageAvailable = true

    const acknowledgement = parseAcknowledgement(acknowledgementKey)
    if (acknowledgement.status === 'unreadable') {
      return 'unreadable' as const
    }
    bootstrapStorageError = false

    if (persistedOutbox.status === 'missing') {
      if (pendingNeedsPersistence && pending) {
        if (!writeOutbox(pending, pendingWriteId)) {
          return 'unreadable' as const
        }
      } else {
        pending = null
        pendingWriteId = null
        conflict = null
        return 'idle' as const
      }
    } else if (pendingNeedsPersistence && pending) {
      const pendingSnapshot = pending
      if (!writeOutbox(pendingSnapshot, pendingWriteId)) {
        return 'unreadable' as const
      }
      persistedOutbox = {
        status: 'value',
        value: { snapshot: pendingSnapshot, writeId: pendingWriteId },
      }
    } else {
      pending = persistedOutbox.value.snapshot
      pendingWriteId = persistedOutbox.value.writeId
      pendingNeedsPersistence = false
    }

    if (acknowledgement.status === 'value') {
      lastAcknowledgedSnapshot = acknowledgement.value.snapshot
    }
    if (
      persistedOutbox.status === 'value' &&
      acknowledgement.status === 'value' &&
      acknowledgesOutboxEntry(persistedOutbox.value, acknowledgement.value)
    ) {
      if (!finishOutboxCleanup(pending!, pendingWriteId, 'acknowledged')) {
        return 'unreadable' as const
      }
      return 'cleaned' as const
    }

    if (persistedOutbox.status === 'missing' && !pending) {
      return 'idle' as const
    }

    const effectiveRevision = remoteRevision ?? 0
    if (
      effectiveRevision > 0 &&
      (pending!.expectedRevision === null ||
        pending!.expectedRevision < effectiveRevision)
    ) {
      conflict = {
        local: pending!,
        remoteRevision: effectiveRevision,
      }
      return 'conflict' as const
    }
    return 'pending' as const
  }

  if (
    initialOutbox.status === 'value' &&
    initialAcknowledgement.status === 'value' &&
    acknowledgesOutboxEntry(initialOutbox.value, initialAcknowledgement.value)
  ) {
    finishOutboxCleanup(
      initialOutbox.value.snapshot,
      initialOutbox.value.writeId,
      'acknowledged',
    )
  }

  const clearTimer = () => {
    if (!timer) return
    clearTimeout(timer)
    timer = null
  }

  const evictIfIdle = () => {
    if (activeConsumers === 0 && !pending && !inFlight && !timer && !conflict) {
      onIdle()
    }
  }

  const schedule = () => {
    if (
      !outboxStorageAvailable ||
      !hydrated ||
      !pending ||
      conflict ||
      bootstrapStorageError ||
      state === 'error'
    )
      return
    clearTimer()
    timer = setTimeout(() => {
      timer = null
      void drain()
    }, DEBOUNCE_MS)
  }

  const drain = async (): Promise<void> => {
    if (!hydrated || inFlight || !pending || conflict) return

    const snapshot = pending
    const writeId = pendingWriteId
    if (
      pendingOutboxCleanup &&
      sameSnapshot(pendingOutboxCleanup.snapshot, snapshot) &&
      pendingOutboxCleanup.writeId === writeId
    ) {
      if (
        !finishOutboxCleanup(snapshot, writeId, pendingOutboxCleanup.reason)
      ) {
        return
      }
      setState(pending ? 'pending' : 'idle')
      evictIfIdle()
      return
    }
    setState('pending')
    const request = Promise.resolve().then(() => currentWriter(snapshot))
    inFlight = request
      .then((result) => {
        if (result.status === 'CONFLICT') {
          const effectiveRevision = observeRemoteRevision(result.revision)
          conflict = {
            local: pending ?? snapshot,
            remoteRevision: effectiveRevision,
          }
          setState('conflict')
          return
        }

        const acknowledgedRevision = observeRemoteRevision(result.revision)
        lastAcknowledgedSnapshot =
          typeof acknowledgedRevision === 'number'
            ? replaceRevision(snapshot, acknowledgedRevision)
            : snapshot
        const acknowledgementWritten = writeAcknowledgement(
          lastAcknowledgedSnapshot,
          writeId,
        )
        if (!acknowledgementWritten) {
          pendingOutboxCleanup = {
            snapshot,
            writeId,
            reason: 'acknowledged',
            acknowledgedSnapshot: lastAcknowledgedSnapshot,
          }
          setState('error')
          return
        }

        if (
          pending &&
          sameSnapshot(pending, snapshot) &&
          pendingWriteId === writeId
        ) {
          const persisted = parseOutbox(outboxKey)
          if (persisted.status === 'unreadable') {
            outboxStorageAvailable = false
            setState('error')
            return
          }
          outboxStorageAvailable = true
          if (
            persisted.status === 'value' &&
            !samePersistedOutbox(persisted.value, snapshot, writeId)
          ) {
            pending =
              typeof acknowledgedRevision === 'number'
                ? replaceRevision(
                    persisted.value.snapshot,
                    acknowledgedRevision,
                  )
                : persisted.value.snapshot
            pendingWriteId = persisted.value.writeId
            if (!writeOutbox(pending, pendingWriteId)) {
              setState('error')
              return
            }
          } else {
            if (!finishOutboxCleanup(snapshot, writeId, 'acknowledged')) {
              return
            }
          }
        } else if (pending && typeof acknowledgedRevision === 'number') {
          pending = replaceRevision(pending, acknowledgedRevision)
          if (!writeOutbox(pending, pendingWriteId)) {
            setState('error')
            return
          }
        }

        setState(pending ? 'pending' : 'idle')
      })
      .catch(() => {
        setState('error')
      })
      .finally(() => {
        inFlight = null
        if (pending && !conflict && state !== 'error') {
          schedule()
        }
        evictIfIdle()
        notify()
      })

    await inFlight
  }

  const coordinator: GridViewPreferenceCoordinator = {
    enqueue: (snapshot) => {
      if (!outboxStorageAvailable) restoreOutbox()
      const nextSnapshot =
        remoteRevision !== null &&
        remoteRevision > 0 &&
        (snapshot.expectedRevision === null ||
          snapshot.expectedRevision < remoteRevision)
          ? replaceRevision(snapshot, remoteRevision)
          : snapshot
      pending = nextSnapshot
      pendingWriteId = createWriteId()
      if (
        pendingOutboxCleanup &&
        (!sameSnapshot(pendingOutboxCleanup.snapshot, nextSnapshot) ||
          pendingOutboxCleanup.writeId !== pendingWriteId)
      ) {
        pendingOutboxCleanup = null
      }
      if (conflict) {
        conflict = { ...conflict, local: nextSnapshot }
      }
      if (!outboxStorageAvailable || !writeOutbox(nextSnapshot)) {
        pendingNeedsPersistence = true
        setState('error')
      } else if (bootstrapStorageError) {
        setState('error')
      } else {
        setState(conflict ? 'conflict' : 'pending')
      }
      schedule()
    },
    flush: async () => {
      clearTimer()
      if (!hydrated) return
      while (inFlight || (pending && !conflict && state !== 'error')) {
        if (inFlight) {
          await inFlight
        } else {
          await drain()
        }
      }
    },
    retry: async () => {
      if (state !== 'error') return
      if (bootstrapStorageError) {
        const recoveryState = recoverBootstrapStorage()
        if (recoveryState === 'unreadable') {
          setState('error')
          return
        }
        if (recoveryState === 'idle' || recoveryState === 'cleaned') {
          setState('idle')
          evictIfIdle()
          return
        }
        if (recoveryState === 'conflict') {
          setState('conflict')
          return
        }
        if (recoveryState === 'pending' && pending) {
          setState('pending')
          await coordinator.flush()
          return
        }
      }
      if (!restoreOutbox() || !pending) {
        setState('error')
        return
      }
      if (
        pendingOutboxCleanup &&
        sameSnapshot(pendingOutboxCleanup.snapshot, pending) &&
        pendingOutboxCleanup.writeId === pendingWriteId
      ) {
        const cleanup = pendingOutboxCleanup
        const persisted = parseOutbox(outboxKey)
        if (persisted.status === 'unreadable') {
          outboxStorageAvailable = false
          setState('error')
          return
        }
        outboxStorageAvailable = true
        if (
          persisted.status === 'value' &&
          !samePersistedOutbox(
            persisted.value,
            cleanup.snapshot,
            cleanup.writeId,
          )
        ) {
          pending =
            remoteRevision !== null &&
            (persisted.value.snapshot.expectedRevision === null ||
              persisted.value.snapshot.expectedRevision < remoteRevision)
              ? replaceRevision(persisted.value.snapshot, remoteRevision)
              : persisted.value.snapshot
          pendingWriteId = persisted.value.writeId
          pendingOutboxCleanup = null
          if (cleanup.reason === 'discarded') conflict = null
          setState('pending')
          schedule()
          return
        }
        if (
          cleanup.reason === 'acknowledged' &&
          cleanup.acknowledgedSnapshot &&
          !writeAcknowledgement(cleanup.acknowledgedSnapshot, cleanup.writeId)
        ) {
          setState('error')
          return
        }
        if (
          !finishOutboxCleanup(
            cleanup.snapshot,
            cleanup.writeId,
            cleanup.reason,
          )
        ) {
          return
        }
        if (cleanup.reason === 'discarded') conflict = null
        setState('idle')
        evictIfIdle()
        return
      }
      const persisted = parseOutbox(outboxKey)
      if (persisted.status === 'unreadable') {
        outboxStorageAvailable = false
        setState('error')
        return
      }
      outboxStorageAvailable = true
      if (
        !pendingNeedsPersistence &&
        persisted.status === 'value' &&
        !samePersistedOutbox(persisted.value, pending, pendingWriteId)
      ) {
        pending = persisted.value.snapshot
        pendingWriteId = persisted.value.writeId
      }
      if (!writeOutbox(pending)) {
        setState('error')
        return
      }
      setState('pending')
      await coordinator.flush()
    },
    resolveConflict: async (choice, nextRemoteRevision) => {
      if (!conflict) return
      if (choice === 'remote') {
        clearTimer()
        const effectiveRevision = observeRemoteRevision(nextRemoteRevision)
        const localConflict = conflict.local
        pendingOutboxCleanup = {
          snapshot: localConflict,
          writeId: pendingWriteId,
          reason: 'discarded',
        }
        const persisted = parseOutbox(outboxKey)
        if (persisted.status === 'unreadable') {
          outboxStorageAvailable = false
          setState('error')
          return
        }
        outboxStorageAvailable = true
        if (
          persisted.status === 'value' &&
          !samePersistedOutbox(persisted.value, localConflict, pendingWriteId)
        ) {
          pending =
            effectiveRevision !== null &&
            (persisted.value.snapshot.expectedRevision === null ||
              persisted.value.snapshot.expectedRevision < effectiveRevision)
              ? replaceRevision(persisted.value.snapshot, effectiveRevision)
              : persisted.value.snapshot
          pendingWriteId = persisted.value.writeId
          pendingOutboxCleanup = null
          conflict = null
          setState('pending')
          schedule()
          return
        }
        if (!finishOutboxCleanup(localConflict, pendingWriteId, 'discarded')) {
          return
        }
        conflict = null
        setState('idle')
        return
      }

      const revision = observeRemoteRevision(
        nextRemoteRevision ?? conflict.remoteRevision,
      )
      if (revision == null) {
        setState('conflict')
        return
      }
      pending = replaceRevision(conflict.local, revision)
      pendingWriteId = createWriteId()
      conflict = null
      if (!writeOutbox(pending, pendingWriteId)) {
        setState('error')
        return
      }
      setState('pending')
      await coordinator.flush()
    },
    hydrateRemoteRevision: (revision) => {
      const effectiveRevision = observeRemoteRevision(revision) ?? 0
      hydrated = true
      if (bootstrapStorageError) return
      if (pending && pending.expectedRevision !== null) {
        if (pending.expectedRevision < effectiveRevision) {
          conflict = { local: pending, remoteRevision: effectiveRevision }
          setState('conflict')
          return
        }
      }
      if (
        pending &&
        pending.expectedRevision === null &&
        effectiveRevision > 0
      ) {
        conflict = { local: pending, remoteRevision: effectiveRevision }
        setState('conflict')
        return
      }
      if (pending && !conflict) {
        setState('pending')
        schedule()
      }
    },
    setWriter: (nextWriter) => {
      currentWriter = nextWriter
    },
    retain: () => {
      activeConsumers += 1
    },
    release: () => {
      activeConsumers = Math.max(0, activeConsumers - 1)
      evictIfIdle()
    },
    getState: () => state,
    getPendingSnapshot: () => pending,
    getLastAcknowledgedSnapshot: () => lastAcknowledgedSnapshot,
    getConflict: () => conflict,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }

  return coordinator
}

const coordinators = new Map<string, GridViewPreferenceCoordinator>()

export const getGridViewPreferenceCoordinator = ({
  key,
  writer,
}: {
  key: string
  writer: GridViewPreferenceWriter
}) => {
  const existing = coordinators.get(key)
  if (existing) {
    existing.setWriter(writer)
    existing.retain()
    return existing
  }
  const coordinator = createCoordinator({
    key,
    writer,
    onIdle: () => {
      if (coordinators.get(key) === coordinator) coordinators.delete(key)
    },
  })
  coordinators.set(key, coordinator)
  coordinator.retain()
  return coordinator
}

export const clearGridViewPreferenceCoordinatorsForTests = () => {
  coordinators.clear()
}

export const GRID_VIEW_PREFERENCE_DEBOUNCE_MS = DEBOUNCE_MS
